<template>
  <FreePageLayout>
    <div
      class="fixed top-0 -mx-4 flex h-screen w-screen flex-col"
      style="padding-top: var(--status-bar-height)"
    >
      <FreeBackButton
        class="back-button absolute left-4 top-4 z-10"
        style="margin-top: var(--status-bar-height)"
        @click="
          goBack({
            query: {
              source: 'hot_offers',
              sourceElement: 'back_button',
            },
          })
        "
      />
      <div
        class="pointer-events-none absolute z-20 flex w-full touch-none justify-center"
        style="top: calc(var(--status-bar-height, 0px) + 0.5rem + 9px)"
      >
        <FreedomTravelLogo
          class="text-static-white relative max-h-fit max-w-fit duration-100"
          :class="{
            minified: !isCountriesLoading || hotCountries.length > 0,
          }"
          aria-label="Freedom Travel (Aviata)"
          width="80"
          height="24"
        />
      </div>
      <div
        class="h-64 flex-shrink-0"
        :class="{ 'shimmer-background': isCountriesLoading }"
      >
        <HotOffersCountriesCarousel
          @onActiveSlideChange="onActiveSlideChange"
        />
      </div>
      <div class="w-full flex-1">
        <div
          class="bg-background-neutral-primary relative z-20 h-full px-4 pt-4"
        >
          <CitySelect
            class="mb-3"
            :bottom-sheet-name="CITY_SELECT_BOTTOMSHEET"
            :is-loading="isCountriesLoading"
            only-kz-cities
            @onCitySelect="handleCitySelect"
          >
            <template #trigger>
              <p class="text-text-neutral-primary text-base font-medium">
                {{
                  origCity
                    ? $t('hotOffers.departureFrom', {
                        city: origCity.name,
                      })
                    : $t('hotOffers.selectCity')
                }}
              </p>
            </template>
          </CitySelect>
          <HotOffers @onChangeCity="openCitySelectBottomSheet" />
        </div>
      </div>
    </div>
    <HotOffersRoutesBottomSheet />
  </FreePageLayout>
</template>

<script setup lang="ts">
  import { FreeBackButton, FreePageLayout } from 'freedom-shared';
  import { onMounted } from 'vue';
  import { controllerModel } from 'shared';

  import {
    HotOffersRoutesBottomSheet,
    HotOffersCountriesCarousel,
    hotOffersModel,
  } from '@/features/hot-offers';

  import { CitySelect } from '@/entities/city-select';
  import type { hotOffersTypes } from '@/entities/hot-offers';

  import { analyticsEventsLib } from '@/shared/lib';
  import { useBackNavigation } from '@/shared/model';

  import FreedomTravelLogo from '@/assets/images/freedom-travel-logo-80x24.svg?component';

  import HotOffers from './HotOffers.vue';

  const { goBack } = useBackNavigation();

  const {
    origCity,
    origCityCode,
    destCountryCode,
    isCountriesLoading,
    hotCountries,
    changeDestCountryCode,
    changeOrigCityCode,
  } = hotOffersModel.useHotOffers();

  const CITY_SELECT_BOTTOMSHEET = 'hot-offers-city-select-bottom-sheet-name';
  const bottomSheet = controllerModel.useBottomSheet();

  const openCitySelectBottomSheet = () => {
    bottomSheet.show(CITY_SELECT_BOTTOMSHEET);
  };

  function onActiveSlideChange(
    countryCode: string,
    direction: '<' | '>',
    isInitialChange?: boolean,
  ) {
    if (!isInitialChange) {
      analyticsEventsLib.hotOffersScroll({ direction });
    }
    changeDestCountryCode(countryCode);
  }

  const handleCitySelect = (city: hotOffersTypes.MappedCity) => {
    analyticsEventsLib.hotOffersOriginCityChange({
      origCityCode: city.code,
      source: 'hot_offers',
    });
    changeOrigCityCode(city.code);
  };

  onMounted(() => {
    analyticsEventsLib.hotOffersPageOpen({
      cityFrom: origCityCode.value,
      countryName: destCountryCode.value,
    });
  });
</script>

<style scoped>
  .back-button {
    --return-button-color: theme('colors.static.white.DEFAULT');
  }
</style>
